/** Pure formatting helpers for the product cards. Platform-tier (no project identity). */

type DimensionGroup = { raw: string; unit: string; axes: number[]; cm?: number[] };

/** Render a product's dimension groups for a card: "23.5×23.5×17 inch / 19.25×19.25×15 inch". */
export function dimensionsLabel(groups: DimensionGroup[] | null | undefined): string {
  if (!Array.isArray(groups) || groups.length === 0) return '';
  return groups.map((g) => g.raw).join(' / ');
}

const CATEGORY_TITLES: Record<string, string> = {
  furniture: 'Furniture',
  vases: 'Vases',
  planters: 'Planters',
  wall_clock: 'Wall Clock',
  table_clock: 'Table Clock',
  wall_mirror: 'Wall Mirror',
  table_mirror_frame: 'Table Mirror / Frame',
  trays_bowls: 'Trays & Bowls',
  wall_art: 'Wall Art',
  sculpture_object: 'Sculpture & Object',
  animals_insects: 'Animals & Insects',
  leaf: 'Leaf',
  hurricanes_candles: 'Hurricanes & Candles',
};

/** Pretty category label, e.g. "wall_clock" → "Wall Clock". Falls back to title-casing the slug. */
export function categoryLabel(category: string): string {
  if (CATEGORY_TITLES[category]) return CATEGORY_TITLES[category];
  return category
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * True when a piece's "name" is really a bare SKU / code (the order-line builder falls back to the
 * SKU when no card carried a name). Heuristic: identical to the SKU, or a code-shaped token — mostly
 * digits / dashes / uppercase with no lowercase word (e.g. "GG-10432", "10432", "SKU_88"). Presentation
 * only — used so review/recap rows never surface a raw code as the visible label (R17 Q1: dev-79
 * "just random numbers").
 */
export function isSkuLikeName(name: string, sku?: string): boolean {
  const n = name.trim();
  if (n === '') return true;
  if (sku && n.toLowerCase() === sku.trim().toLowerCase()) return true;
  // R60 (F1 · THE MECHANISM): two or more real WORDS make a real name, whatever the case or digits.
  // The Godgift catalogue's names are ALL-CAPS and frequently carry a set count — "SET OF 3
  // PLANTERS", "SET OF 2 PLANTERS" — so the old "no lowercase AND a digit ⇒ a code" rule condemned
  // genuine product names to the "Selected piece" placeholder. That is the dev-120 hi S6 sheet
  // exactly: `90572`/`90573`/`90574` are all "SET OF n PLANTERS" (a digit → placeholder) while
  // `GGI 18126 LS` = "CAST ALUMINIUM POT PLANTER" (no digit → rendered), and the en session's
  // numerics were table clocks whose names carry no digit either. The chip tray renders the raw
  // name (no heuristic), which is why the SAME four pieces resolved there. Codes have at most ONE
  // letter-run ("GG-10432", "10432", "SKU_88", "0023 LMS"), so this stays a code test.
  const letterWords = n.match(/[A-Za-z]{3,}/g) ?? [];
  if (letterWords.length >= 2) return false;
  // No lowercase letter AND at least one digit → reads as a code, not a product name.
  return !/[a-z]/.test(n) && /\d/.test(n);
}

/**
 * The label to SHOW for an order/recap line: the real piece name when it reads like one, else a
 * graceful category-derived fallback ("Selected piece" when even the category is unknown). Never
 * returns the raw SKU (R17 Q1). Presentation-only — the underlying line data (incl. the SKU for the
 * order payload) is untouched.
 */
export function displayPieceName(name: string, category: string, sku?: string): string {
  if (!isSkuLikeName(name, sku)) return name;
  const cat = category.trim();
  return cat !== '' ? `${categoryLabel(cat)} piece` : 'Selected piece';
}
